import { Link, useNavigate } from 'react-router-dom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const CourseCard = ({
    course,
    isEnrolled = false,
    type = 'COURSE' 
}) => {
    const navigate = useNavigate();
    const { id, title, description, price, originalPrice, thumbnailUrl, level } = course;
    
    // Check expiry
    const isExpired = course.enrollmentStatus === 'EXPIRED';

    // Helper to format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getLinkPath = () => {
        if (type === 'COURSE') {
            // If expired or not enrolled, go to details page to buy. 
            // If active, go to learn page.
            if (isEnrolled && isExpired) return `/courses/${id}`; 
            return isEnrolled ? `/learn/${id}` : `/courses/${id}`;
        }
        const resourceType = course.type || type; 
        if (resourceType === 'NOTES') return `/notes/${id}`;
        if (resourceType === 'MOCK_TESTS') return `/mock-tests/${id}`;
        return `/resources/${id}`;
    };

    const linkPath = getLinkPath();

    const handleCardClick = () => {
        navigate(linkPath);
    };

    const handleActionClick = (e) => {
        e.stopPropagation();
    };

    return (
        <div
            className={`group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer flex flex-col ${isExpired ? 'opacity-80 grayscale-[0.2]' : ''}`}
            onClick={handleCardClick}
        >
            <div className="relative overflow-hidden">
                <img
                    src={thumbnailUrl || 'https://placehold.co/400x250/475569/ffffff?text=Resource'}
                    alt={title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                
                {/* Level Badge (if exists) */}
                {level && (
                    <div className="absolute top-3 left-3 bg-slate-900 text-white px-3 py-1 rounded-md text-xs font-medium">
                        {level}
                    </div>
                )}

                {/* Status Badges for Enrolled Courses */}
                {isEnrolled && type === 'COURSE' && (
                    <div className="absolute top-3 right-3 flex gap-2">
                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-md shadow-sm flex items-center gap-1 ${
                            isExpired 
                                ? 'bg-red-100 text-red-700 border border-red-200' 
                                : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                        }`}>
                            {isExpired ? <WarningIcon sx={{ fontSize: 14 }} /> : <CheckCircleIcon sx={{ fontSize: 14 }} />}
                            {isExpired ? 'Expired' : 'Active'}
                        </span>
                    </div>
                )}
                
                {/* Resource Type Badge */}
                {type !== 'COURSE' && (
                     <div className="absolute top-3 right-3 bg-slate-900/80 text-white px-3 py-1 rounded-md text-xs font-medium backdrop-blur-sm">
                        {course.type === 'MOCK_TESTS' ? 'Test' : 'Note'}
                     </div>
                )}
            </div>

            <div className="p-6 flex-1 flex flex-col">
                <div className="mb-3">
                    <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-slate-700 transition-colors duration-200">
                        {title}
                    </h3>
                </div>

                {/* Show Expiry Date if Enrolled, otherwise description */}
                {isEnrolled && type === 'COURSE' && course.expiryDate ? (
                     <div className={`mb-4 p-2 rounded-lg text-xs font-medium border flex items-center gap-2 ${
                        isExpired ? 'bg-red-50 border-red-100 text-red-700' : 'bg-slate-50 border-slate-100 text-slate-600'
                     }`}>
                        <CalendarTodayIcon sx={{ fontSize: 14 }} />
                        {isExpired ? 'Expired on: ' : 'Expires: '} {formatDate(course.expiryDate)}
                     </div>
                ) : (
                    <p className="text-sm text-slate-600 mb-4 line-clamp-2 flex-1">
                        {description?.substring(0, 100)}...
                    </p>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-slate-200 mt-auto">
                    <div className="flex items-center gap-2">
                        {/* Show price if not enrolled OR if expired (needs renewal) */}
                        {(!isEnrolled || isExpired) ? (
                            <>
                                {originalPrice && price < originalPrice && (
                                    <span className="text-sm text-slate-500 line-through">₹{originalPrice}</span>
                                )}
                                <span className="text-xl font-bold text-slate-900">₹{price}</span>
                            </>
                        ) : (
                            <span className="text-sm font-semibold text-emerald-600"></span>
                        )}
                    </div>

                    <div className="flex-shrink-0">
                        <Link
                            to={linkPath}
                            className={`inline-flex items-center justify-center px-4 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                                isEnrolled 
                                    ? isExpired 
                                        ? 'text-white bg-red-600 hover:bg-red-700' // Expired Button
                                        : 'text-white bg-slate-900 hover:bg-slate-800' // Active Button
                                    : 'text-slate-700 bg-white border border-slate-200 hover:bg-slate-50' // Default Button
                            }`}
                            onClick={handleActionClick}
                        >
                            {isEnrolled ? (isExpired ? 'Renew' : 'Open') : 'View Details'}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseCard;